import { memo, useMemo, useState } from 'react';
import { AnalyticsCommonTypes, PracticeAnalyticsAggregations, PracticeAnalyticsTypes } from '@frontend/api-analytics';
import { AnalyticsHooks } from '@frontend/analytics-shared';
import { LocationChip } from '@frontend/analytics-shared';
import { BarChartData, Chart, YAxisLabelValueTick } from '@frontend/charts';
import { formatters } from '@frontend/shared';
import { usePracticeAnalyticsShallowStore } from './hooks/use-practice-analytics-store';

type Props = PracticeAnalyticsTypes.MetricProps & {
  colors: AnalyticsCommonTypes.StringRecord;
  data?: ReturnType<typeof PracticeAnalyticsAggregations.recapturedPatientsSummary>;
  isLoading?: boolean;
  labels: AnalyticsCommonTypes.StringRecord;
};

export const RecapturedMetricMulti = memo(({ colors, data, isLoading, labels }: Props) => {
  const { isDemoAccount } = usePracticeAnalyticsShallowStore('isDemoAccount');
  const { locationNames } = AnalyticsHooks.useAnalyticsOrgLocations({ isDemoAccount, module: 'PA' });
  const [isLargeChart, setIsLargeChart] = useState<boolean>(false);

  const chartsData = useMemo(() => {
    const entries = Object.entries(data?.rawData ?? {});
    const totals = {
      leadTime: 0,
      patients: 0,
      production: 0,
    };

    const patients: BarChartData = {
      groups: entries.map(([locationId, locationDetails]) => {
        const leadTime = locationDetails?.location.recapturedPatients.totals?.leadTime ?? 0;
        const patients = locationDetails?.location.recapturedPatients.totals?.patients ?? 0;
        totals.leadTime += leadTime;
        totals.patients += patients;

        return {
          name: locationNames[locationId] || locationId,
          values: {
            patients,
            leadTime,
          },
        };
      }),
    };

    const production: BarChartData = {
      groups: entries.map(([locationId, locationDetails]) => {
        const production = locationDetails?.location.recapturedPatients.totals?.production ?? 0;
        totals.production += production;

        return {
          name: locationNames[locationId] || locationId,
          values: {
            production,
          },
        };
      }),
    };

    return {
      patients,
      production,
      totals: {
        ...totals,
        leadTime: totals.leadTime / entries.length,
      },
    };
  }, [locationNames, data?.rawData]);

  const renderChart = (data: BarChartData, isProductionChart?: boolean) => (
    <Chart colors={colors} isLoading={isLoading} labels={labels}>
      <Chart.BarChart
        appearance={{
          collectiveTooltip: true,
          customTooltipTitle: ({ groupName }) => <LocationChip locationName={groupName} noTruncating />,
          customXAxisTickFormat: isProductionChart ? (value) => formatters.shortenCurrency(value) : undefined,
          customYAxisTick: ({ labels, groupName, ...rest }) => {
            return (
              <YAxisLabelValueTick
                {...rest}
                label={labels?.[groupName] || groupName}
                onChartSizeChange={(isLargeChart) => setIsLargeChart(isLargeChart)}
                xTransitionLabel={-190}
              />
            );
          },
          groupsGap: 24,
          layout: 'vertical',
          margin: { left: isLargeChart ? 140 : 40 },
          mode: isProductionChart ? 'grouped' : 'stacked',
          showGridLines: true,
          showXAxis: true,
          showYAxis: true,
        }}
        data={data}
        formatValue={isProductionChart ? formatters.currency : formatters.value}
      />
      <Chart.Legends
        formatValue={isProductionChart ? formatters.currency : formatters.value}
        values={chartsData.totals}
      />
    </Chart>
  );

  return (
    <>
      {renderChart(chartsData.patients)}
      {renderChart(chartsData.production, true)}
    </>
  );
});

RecapturedMetricMulti.displayName = 'RecapturedMetricMulti';
